import React, { useState, useMemo, useEffect } from 'react'; // Importamos useEffect

import { Building, PlusCircle, Search, Edit2, Trash2, X, Phone, MapPin, User, Check } from 'lucide-react';

// --- DATOS DE EJEMPLO (MOCK DATA) ---
// Estos datos solo se usan la primera vez que la aplicación se carga
// si no hay datos guardados en localStorage.
const INITIAL_CLINICAS_DATA = [
    { id: 1, nombre: 'Hospital Central Metropolitano', estado: 'Ciudad de México', ciudad: 'Cuauhtémoc', telefono: '55-1234-5678', director: 'Dr. Javier Soto', direccion: 'Av. Reforma #100' },
    { id: 2, nombre: 'Clínica San Vicente', estado: 'Estado de México', ciudad: 'Nezahualcóyotl', telefono: '55-9876-5432', director: 'Dra. María Lopez', direccion: 'Calle 5 #200' },
    { id: 3, nombre: 'Centro Médico del Valle', estado: 'Jalisco', ciudad: 'Guadalajara', telefono: '33-2233-4455', director: 'Dr. Ricardo Mendiola', direccion: 'Eje 1 Poniente #50' },
    { id: 4, nombre: 'Hospital IMSS #45', estado: 'Nuevo León', ciudad: 'Monterrey', telefono: '81-5678-1234', director: 'Dra. Ana Torres', direccion: 'Blvd. Constitución #30' },
    { id: 5, nombre: 'Unidad de Diagnóstico Querétaro', estado: 'Querétaro', ciudad: 'Santiago de Querétaro', telefono: '442-1122-3344', director: 'Lic. Elena Díaz', direccion: 'Avenida del Sol #1' },
];

const EMPTY_CLINICA = {
    id: null,
    nombre: '',
    estado: '',
    ciudad: '',
    telefono: '',
    director: '',
    direccion: '',
};

// Función auxiliar para cargar los datos iniciales
const getInitialData = () => {
    // 1. Intentamos obtener la cadena JSON del localStorage
    const localData = localStorage.getItem('clinicasData');
    
    // 2. Si existe, la parseamos y la retornamos.
    if (localData) {
        return JSON.parse(localData);
    }
    
    // 3. Si no existe, usamos los datos de ejemplo iniciales.
    return INITIAL_CLINICAS_DATA;
};

// *****************************************************************
// COMPONENTE PRINCIPAL
// *****************************************************************
const Clinicas = () => {
  // Inicializamos el estado usando la función getInitialData
  const [clinicas, setClinicas] = useState(getInitialData); 
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentClinica, setCurrentClinica] = useState(EMPTY_CLINICA);
  const [isEditing, setIsEditing] = useState(false);

  // EFECTO: Sincroniza el estado 'clinicas' con localStorage cada vez que cambia.
  useEffect(() => {
    // Convertimos el array a una cadena JSON y lo guardamos
    localStorage.setItem('clinicasData', JSON.stringify(clinicas));
  }, [clinicas]); // Se ejecuta cada vez que el array 'clinicas' se actualiza

  // Lógica de filtrado de datos (LECTURA - R de CRUD)
  const filteredClinicas = useMemo(() => {
    if (!searchTerm) {
      return clinicas;
    }

    const lowerCaseSearch = searchTerm.toLowerCase();

    return clinicas.filter(clinica => {
      // Filtra si el término de búsqueda coincide con varios campos
      return (
        clinica.nombre.toLowerCase().includes(lowerCaseSearch) ||
        clinica.estado.toLowerCase().includes(lowerCaseSearch) ||
        clinica.ciudad.toLowerCase().includes(lowerCaseSearch) ||
        clinica.director.toLowerCase().includes(lowerCaseSearch)
      );
    });
  }, [searchTerm, clinicas]); 

  // ******************************
  // LÓGICA CRUD
  // ******************************

  // Función para abrir el modal en modo creación
  const handleAdd = () => {
    setCurrentClinica(EMPTY_CLINICA);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  // Función para abrir el modal en modo edición (ACTUALIZAR - U de CRUD)
  const handleEdit = (clinica) => {
    setCurrentClinica(clinica);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  // Función para guardar (Crear o Actualizar) (CREACIÓN/ACTUALIZACIÓN - C/U de CRUD)
  const handleSave = (e, formData) => {
    e.preventDefault();
    if (!formData.nombre || !formData.director) {
        // En lugar de alert(), manejar error en la UI o usar console.error
        console.error("Error: Por favor, completa el nombre y el director.");
        return;
    }

    if (isEditing) {
      // Actualizar
      setClinicas(clinicas.map(c => c.id === formData.id ? formData : c));
    } else {
      // Crear (se asigna un ID temporal usando Date.now())
      const newClinica = { ...formData, id: Date.now() };
      setClinicas([...clinicas, newClinica]);
    }
    
    setIsModalOpen(false);
    setCurrentClinica(EMPTY_CLINICA);
  };

  // Función para eliminar (ELIMINACIÓN - D de CRUD)
  const handleDelete = (id) => {
    // Usamos el confirm nativo (reemplázalo con un modal si es necesario en tu entorno)
    if (window.confirm('¿Estás seguro de que quieres eliminar esta clínica?')) {
      setClinicas(clinicas.filter(c => c.id !== id));
    }
  };


  // ******************************
  // COMPONENTE MODAL DE FORMULARIO
  // ******************************
  const ClinicaFormModal = ({ isOpen, onClose, clinica, onSave, isEditing }) => {
    const [formData, setFormData] = useState(clinica);

    // Sincroniza los datos del formulario si la clínica a editar cambia
    useEffect(() => {
        setFormData(clinica);
    }, [clinica]);

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        onSave(e, formData); // Pasamos el evento y los datos al manejador principal
        // El onClose se manejará en onSave, pero lo dejamos aquí como fallback si es necesario
    };

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden transform transition-all">
          <header className="flex justify-between items-center p-5 border-b bg-teal-500 text-white">
            <h2 className="text-xl font-bold">{isEditing ? 'Editar Clínica' : 'Añadir Nueva Clínica'}</h2>
            <button onClick={onClose} className="text-white hover:text-gray-100 p-1 rounded-full hover:bg-teal-600 transition">
              <X className="w-6 h-6" />
            </button>
          </header>

          <form onSubmit={handleSubmit}>
            <div className="p-6 grid grid-cols-1 gap-4">
              
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre de la Clínica *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              {/* Director */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Director(a) *</label>
                <input
                  type="text"
                  name="director"
                  value={formData.director}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Estado */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Estado</label>
                  <input
                    type="text"
                    name="estado"
                    value={formData.estado}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                {/* Ciudad */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Ciudad</label>
                  <input
                    type="text"
                    name="ciudad"
                    value={formData.ciudad}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>

              {/* Dirección */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Dirección Completa</label>
                <input
                  type="text"
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              
              {/* Teléfono */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Teléfono</label>
                <input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

            </div>

            <footer className="p-5 border-t bg-gray-50 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="py-2 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 transition duration-150 shadow-sm"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex items-center py-2 px-4 rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition duration-150 shadow-md"
              >
                <Check className="w-4 h-4 mr-2" />
                {isEditing ? 'Guardar Cambios' : 'Crear Clínica'}
              </button>
            </footer>
          </form>
        </div>
      </div>
    );
  };
  
  // ******************************
  // RENDERIZADO PRINCIPAL
  // ******************************
  return (
    <div className="p-4 sm:p-8 bg-gray-50 min-h-screen">
      
      <header className="mb-6 flex justify-between items-center">
        <div>
            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center">
                <Building className="w-7 h-7 mr-2 text-teal-600" />
                Directorio de Clínicas y Hospitales
            </h1>
            <p className="text-gray-500 mt-1">Gestión completa de las instituciones asociadas (CRUD local y persistente).</p>
        </div>
        <button
            onClick={handleAdd}
            className="flex items-center bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-200 transform hover:scale-[1.02]"
        >
            <PlusCircle className="w-5 h-5 mr-2" />
            Añadir Clínica
        </button>
      </header>

      {/* Barra de Búsqueda */}
      <div className="mb-6 relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar por Nombre, Estado, Ciudad o Director..."
          className="w-full p-3 pl-12 border border-gray-300 rounded-xl shadow-inner focus:ring-teal-500 focus:border-teal-500 transition duration-150"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Resultados de la Búsqueda / Tabla */}
      <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-teal-50">
                <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-teal-700 uppercase tracking-wider">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teal-700 uppercase tracking-wider">Ubicación</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teal-700 uppercase tracking-wider">Contacto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-teal-700 uppercase tracking-wider">Director</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-teal-700 uppercase tracking-wider">Acciones</th>
                </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {filteredClinicas.length > 0 ? (
                filteredClinicas.map((clinica) => (
                    <tr key={clinica.id} className="hover:bg-teal-50 transition duration-100">
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                            {clinica.nombre}
                            <p className="text-xs text-gray-500 flex items-center mt-1 font-normal">
                                <MapPin className='w-3 h-3 mr-1'/> {clinica.direccion || 'N/A'}
                            </p>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                            {clinica.ciudad || 'N/A'}, {clinica.estado || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 flex items-center">
                            <Phone className='w-4 h-4 mr-2 text-teal-500 flex-shrink-0'/> {clinica.telefono || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 flex items-center">
                            <User className='w-4 h-4 mr-2 text-teal-500 flex-shrink-0'/> {clinica.director}
                        </td>
                        <td className="px-6 py-4 text-center whitespace-nowrap">
                            <button 
                                onClick={() => handleEdit(clinica)}
                                className="text-teal-600 hover:text-teal-900 mr-3 p-2 rounded-full hover:bg-teal-100 transition duration-150 shadow-sm"
                                title="Editar"
                            >
                                <Edit2 className="h-4 w-4" />
                            </button>
                            <button 
                                onClick={() => handleDelete(clinica.id)}
                                className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-100 transition duration-150 shadow-sm"
                                title="Eliminar"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </td>
                    </tr>
                ))
                ) : (
                <tr>
                    <td colSpan="5" className="px-6 py-10 text-center text-sm text-gray-500 bg-gray-50">
                        <p className='text-lg font-medium mb-1'>No se encontraron clínicas.</p>
                        <p>Intenta una búsqueda diferente o añade una nueva clínica.</p>
                    </td>
                </tr>
                )}
            </tbody>
            </table>
        </div>
      </div>
      
      {/* Modal de Formulario */}
      <ClinicaFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        clinica={currentClinica}
        onSave={handleSave}
        isEditing={isEditing}
      />
    </div>
  );
};

export default Clinicas;